import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import LinearProgress from '@material-ui/core/LinearProgress';
import Slide from '@material-ui/core/Slide';
import Typography from '@material-ui/core/Typography';
import { uploadImage } from 'actionCreators/imageSelector';
import { pushMessage } from 'actionCreators/messages';
import Toast from 'components/Shared/Toast';
import { WithStyles } from 'decorators/withStyles';
import React, { Fragment, PureComponent } from 'react';
import Cropper from 'react-easy-crop';
import { connect } from 'react-redux';
import { imageCompress } from 'utils';
import { IS_MOBILE } from 'settings';

import ImageReader, { IImageReaderResult } from './ImageReader';
import { Slider } from '@material-ui/core';

interface IState {
  image?: IImageReaderResult;
  saving: boolean;
  progress: number;
  dimentions?: { height: number };
  crop: ICrop;
  zoom: number;
  croppedArea: ICrop;
}

interface IProps {
  opened: boolean;
  width: number;
  height: number;
  onComplete: (image: string) => void;
  classes?: any;

  uploadImage?: typeof uploadImage;
  pushMessage?: typeof pushMessage;
}

interface ICrop {
  width?: number;
  height?: number;
  x: number;
  y: number;
}

@WithStyles({
  imageContainer: {
    width: '100%',
    height: '100%',
  },
  cropper: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
})
class ImageSelector extends PureComponent<IProps, IState> {
  canvas: HTMLCanvasElement;
  resizeTimeout: NodeJS.Timer;
  uploadSubscription: any;
  cropper: any;

  private maxZoom: number = IS_MOBILE ? 10 : 3;

  constructor(props: IProps) {
    super(props);
    this.state = {
      image: null,
      saving: false,
      progress: 0,
      crop: { x: 0, y: 0 },
      zoom: 1,
      croppedArea: null,
    };
  }

  onExited = () => {
    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
    }
    this.setState({ image: null, saving: false, progress: 0 });
  };

  handleSave = async () => {
    const { image, croppedArea } = this.state;
    const { width, height, x, y } = croppedArea;

    this.setState({ saving: true, progress: 0 });
    const croppedImage = await imageCompress(image.url, width, height, x, y);

    try {
      this.setState({ saving: true });
      const result: any = await this.props.uploadImage(croppedImage);
      this.props.onComplete(result);
    } catch (err) {
      this.props.pushMessage('Não foi possivel salvar a imagem', 'error');
    } finally {
      this.setState({ saving: false });
    }
  };

  handleCancel = () => {
    this.props.onComplete(null);
  };

  setImage = (image: IImageReaderResult) => {
    this.setState({ image: null });

    const dimentions = this.calculateRegion(image.width, image.height);
    this.setState({ image, dimentions });
  };

  calculateRegion = (width: number, height: number): IState['dimentions'] => {
    const dialogWidth = window.innerWidth - 180;
    const dialogHeight = window.innerHeight - 360;

    if (height <= dialogHeight && width <= dialogWidth) {
      height = Math.floor(height);

      height = height < 280 ? 280 : height;

      return { height };
    }

    if (width > dialogWidth) {
      const widthRatio = dialogWidth / width;
      return this.calculateRegion(dialogWidth, height * widthRatio);
    }

    const heightRatio = dialogHeight / height;
    return this.calculateRegion(width * heightRatio, dialogHeight);
  };

  onCropComplete = (crop: any, croppedArea: ICrop) => this.setState({ croppedArea });

  onCropChange = (crop: ICrop) => this.setState({ crop });

  onZoomChange = (...params: any) => {
    if (params.length === 1) this.setState({ zoom: params[0] });
    else this.setState({ zoom: params[1] });
  };

  render() {
    const { image, dimentions, saving, progress, crop, zoom } = this.state;
    const { classes, opened, width, height } = this.props;

    return (
      <Fragment>
        <Dialog
          fullScreen={IS_MOBILE}
          open={opened}
          maxWidth={false}
          disableBackdropClick
          disableEscapeKeyDown
          onExited={this.onExited}
          TransitionComponent={Transition}
        >
          <DialogTitle>
            <Grid container spacing={3} alignContent='center'>
              <Grid item xs={true}>
                Selecionar Imagem
                <Typography variant='body1'>
                  <strong>Tamanho sugerido:</strong> {height}px de altura {width}px de largura
                </Typography>
              </Grid>
              {image && !saving && (
                <Grid item xs={false}>
                  <ImageReader onLoad={this.setImage} />
                </Grid>
              )}
            </Grid>
            <Slider value={zoom} min={1} max={this.maxZoom} onChange={this.onZoomChange} />
          </DialogTitle>
          <DialogContent className={classes.content}>
            {!image && <ImageReader onLoad={this.setImage} droppable />}

            {image && (
              <div
                className={`${classes.imageContainer} ${saving ? classes.imageContainerSaving : ''}`}
                style={IS_MOBILE ? null : dimentions}
              >
                {saving && (
                  <LinearProgress
                    color='secondary'
                    variant={progress > 0 && progress < 100 ? 'determinate' : 'indeterminate'}
                    value={progress}
                  />
                )}
                <div className={classes.cropper}>
                  <Cropper
                    image={image.url}
                    crop={crop}
                    cropSize={{
                      width,
                      height,
                    }}
                    zoom={zoom}
                    cropShape='rect'
                    restrictPosition={false}
                    maxZoom={this.maxZoom}
                    onCropChange={this.onCropChange}
                    onCropComplete={this.onCropComplete}
                    onZoomChange={this.onZoomChange}
                  />
                </div>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleCancel} id='image-selector-button-cancel'>
              Cancelar
            </Button>
            <Button
              disabled={!image || saving}
              color='secondary'
              onClick={this.handleSave}
              id='image-selector-button-OK'
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
        <Toast />
      </Fragment>
    );
  }
}

const mapStateToProps = () => ({});

export default connect<{}, {}, IProps>(mapStateToProps, { uploadImage, pushMessage })(ImageSelector);

function Transition(props: any) {
  return <Slide direction='up' {...props} />;
}
